import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "@me8eon/translation";
import { useJwtToken } from "@me8eon/react_authentication";
import useOnboardingPortalStore from "../OnboardingPortal.Store";
import { EonUiPagination } from "@eon-ui/eon-ui-components-react";
import { useLanguage } from "@me8eon/language";
import OnboardingPortalLinkArrow from "../components/OnboardingPortalLinkArrow";
import {
    exportAppsDataAsCsv,
    exportAppsDataAsExcel,
} from "../OnboardingPortal.export.utils";
import OnboardingPortalAppIcon from "../components/OnboardingPortalAppIcon";
import OnboardingPortalTableHeaders from "../components/OnboardingPortalTableHeaders";
import OnboardingPortalStatusFilter from "../components/OnboardingPortalStatusFilter";
import OnboardingPortalOwnerFilter from "../components/OnboardingPortalOwnerFilter";
import OnboardingPortalSearchFilter from "../components/OnboardingPortalSearchFilter";
import OnboardingPortalExportButton from "../components/OnboardingPortalExportButton";
import OnboardingPortalLoader from "../components/OnboardingPortalLoader";
import OnboardingPortalRefreshButton from "../components/OnboardingPortalRefreshButton";

const ReviewAppsScreen = () => {
    const t = useTranslation();
    const jwt = useJwtToken();
    const {
        showModule,
        reviewAllApps,
        fetchReviewApps,
        onReviewApp,
        handleReviewApp,
        onViewApp,
        isLoadingReviewApps,
        isSubmittingReviewApp,
    } = useOnboardingPortalStore();
    const [lang] = useLanguage();
    const [owner, setOwner] = useState("");
    const [search, setSearch] = useState("");
    const [status, setStatus] = useState("pending");
    const [pageSize, setPageSize] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const filteredApps = useMemo(
        () =>
            reviewAllApps.filter((app) => {
                const matchesSearch =
                    app.title.toLowerCase().includes(search.toLowerCase()) ||
                    app.subTitle.toLowerCase().includes(search.toLowerCase());
                const matchesStatus =
                    status === "all" ? true : app.status === status;
                const matchesOwner = owner === "" ? true : app.owner === owner;
                return matchesSearch && matchesStatus && matchesOwner;
            }),
        [search, status, owner, reviewAllApps]
    );

    useEffect(() => {
        const cp = currentPage;
        const ps = pageSize;
        setCurrentPage(1);
        if (cp > 1 && Math.ceil(filteredApps.length / ps) < cp) {
            setCurrentPage(1);
        }
    }, [filteredApps]);

    useEffect(() => {
        if (showModule === "review-apps") {
            fetchReviewApps(jwt);
        }
    }, [showModule]);
    return (
        <>
            <div className="onboarding-portal--content--card">
                {(isLoadingReviewApps || isSubmittingReviewApp) && (
                    <OnboardingPortalLoader />
                )}
                <div className="onboarding-portal--content--card--header">
                    <div className="onboarding-portal--content--card--header--text">
                        {t("onboardingPortal.tableHeaders.apps")} (
                        {filteredApps.length})
                    </div>
                    <div className="onboarding-portal--content--card--header--actions">
                        <OnboardingPortalRefreshButton
                            onClick={() => fetchReviewApps(jwt)}
                        />
                        <OnboardingPortalSearchFilter
                            t={t}
                            search={search}
                            setSearch={setSearch}
                        />
                        <OnboardingPortalStatusFilter
                            t={t}
                            status={status}
                            setStatus={setStatus}
                        />
                        <OnboardingPortalOwnerFilter
                            t={t}
                            owner={owner}
                            setOwner={setOwner}
                            list={filteredApps}
                        />
                        <OnboardingPortalExportButton
                            t={t}
                            onExportCSV={() =>
                                exportAppsDataAsCsv(filteredApps, "apps_export")
                            }
                            onExportExcel={() =>
                                exportAppsDataAsExcel(
                                    filteredApps,
                                    "apps_export"
                                )
                            }
                        />
                    </div>
                </div>
                <table className="onboarding-portal--table">
                    <OnboardingPortalTableHeaders
                        headers={[
                            "onboardingPortal.tableHeaders.icon",
                            "onboardingPortal.tableHeaders.title",
                            "onboardingPortal.tableHeaders.subTitle",
                            "onboardingPortal.tableHeaders.status",
                            "onboardingPortal.tableHeaders.owner",
                            "onboardingPortal.tableHeaders.action",
                        ]}
                        t={t}
                    />
                    <tbody>
                        {filteredApps
                            .slice(
                                (currentPage - 1) * pageSize,
                                currentPage * pageSize
                            )
                            .map((app) => (
                                <tr key={app.id}>
                                    <td>
                                        <OnboardingPortalAppIcon
                                            icon={app.icon}
                                            iconUrl={app.iconDataUrl}
                                        />
                                    </td>
                                    <td>{app.title}</td>
                                    <td>{app.subTitle}</td>
                                    <td>
                                        <span
                                            className={`status--${app.status}`}
                                        >
                                            {t(
                                                `onboardingPortal.statusLabel.${app.status}`
                                            )}
                                        </span>
                                    </td>
                                    <td>{app.owner}</td>
                                    <td>
                                        <div className="actions-row">
                                            {app.status === "pending" && (
                                                <>
                                                    <span
                                                        className="action--approve"
                                                        onClick={() =>
                                                            onReviewApp(app)
                                                        }
                                                    >
                                                        {t(
                                                            "onboardingPortal.approveBtn"
                                                        )}
                                                    </span>
                                                    <span
                                                        className="action--reject"
                                                        onClick={() =>
                                                            onReviewApp(app)
                                                        }
                                                    >
                                                        {t(
                                                            "onboardingPortal.rejectBtn"
                                                        )}
                                                    </span>
                                                    <OnboardingPortalLinkArrow
                                                        onClick={() => {
                                                            onReviewApp(app);
                                                        }}
                                                        noSpace={false}
                                                        size="small"
                                                    />
                                                </>
                                            )}
                                            {app.status !== "pending" && (
                                                <span
                                                    className="action--view"
                                                    onClick={() =>
                                                        onViewApp(app)
                                                    }
                                                >
                                                    {t(
                                                        "onboardingPortal.viewBtn"
                                                    )}
                                                </span>
                                            )}
                                            {app.status === "approved" && (
                                                <span
                                                    className="action--approve"
                                                    onClick={() => {
                                                        handleReviewApp(
                                                            jwt,
                                                            app?.id || "",
                                                            "inQA",
                                                            "Moved to QA"
                                                        );
                                                    }}
                                                >
                                                    {t(
                                                        "onboardingPortal.toQABtn"
                                                    )}
                                                </span>
                                            )}
                                            {app.status === "inQA" && (
                                                <span
                                                    className="action--approve"
                                                    onClick={() => {
                                                        handleReviewApp(
                                                            jwt,
                                                            app?.id || "",
                                                            "inProd",
                                                            "Moved to Production"
                                                        );
                                                    }}
                                                >
                                                    {t(
                                                        "onboardingPortal.toProdBtn"
                                                    )}
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                    </tbody>
                </table>
            </div>
            <EonUiPagination
                lang={lang}
                begin={
                    filteredApps.length === 0 ? 0 : (currentPage - 1) * pageSize
                }
                currentPage={currentPage}
                end={Math.min(currentPage * pageSize, filteredApps.length)}
                initialPageSize={pageSize}
                pageSize={pageSize}
                pageSizes={"5,10,20,50,100"}
                totalItems={filteredApps.length}
                onPageChanged={(e) => {
                    setCurrentPage(e.detail as any);
                    console.log(e.detail);
                }}
                onPageSizeChanged={(e) => {
                    setPageSize(e.detail);
                    setCurrentPage(1);
                }}
                onPageDecreased={(e) => {
                    console.log(e);
                    setCurrentPage((prev) => Math.max(prev - 1, 1));
                }}
                onPageIncreased={(e) => {
                    console.log(e);
                    setCurrentPage((prev) =>
                        Math.min(
                            prev + 1,
                            Math.ceil(filteredApps.length / pageSize)
                        )
                    );
                }}
            />
        </>
    );
};

export default ReviewAppsScreen;
